/**
 * Bumped to 3 on 2026-09-05, for the MMORPG pivot: hitscan, ammo, aiming, prone, lag
 * compensation, the raid clock, weather and extraction all left the wire, and a body yaw, a
 * selected target, six ability slots, a level, a max HP and a packed gear look arrived. The whole
 * contract is written down in `docs/PROTOCOL-V3.md`.
 */
export const PROTOCOL_VERSION = 3;
export const TICK_RATE = 30;
export const TICK_DT = 1 / TICK_RATE;
export const INTERP_DELAY_MS = 100;
/** Default cap for a one-shot room (a test yard, the Spine). Persistent maps set their own. */
export const MAX_PLAYERS_PER_ROOM = 12;
/**
 * Default cap for a persistent map that does not name one. 500 is the city's number and the one
 * the perf budget is written against (`docs/PERF-BUDGET.md`); a zone overrides it downward.
 */
export const PERSISTENT_MAX_PLAYERS = 500;
export const MAX_INPUTS_PER_MESSAGE = 8;

/** The default map a player with no saved position is put into. */
export const DEFAULT_MAP_ID = 'lanternfall';

// ---------------------------------------------------------------------------------------------
// Tab-target combat (v3)
// ---------------------------------------------------------------------------------------------

/** Ability slots per Calling. Three of them are authored; 3..5 are Phase 2b's. */
export const ABILITY_SLOTS = 6;
/**
 * The global cooldown: pressing any ability that respects it locks the whole bar for this long.
 * It is what stops six instants landing in one tick, and it is the metronome the whole class
 * design is written against — one number, shared by the server that enforces it and the client
 * that draws the sweep.
 */
export const GLOBAL_COOLDOWN_MS = 1500;
/** How long after taking or dealing damage a player still reads as `SnapFlags.InCombat`. */
export const IN_COMBAT_MS = 5000;
/**
 * Fraction of the **current level's XP span** lost on death, floored at that level's threshold
 * so a death can never take a level away (owner's rule, 2026-09-05). Replaces the old
 * half-the-run's-XP penalty, which had no meaning once runs stopped existing.
 */
export const DEATH_XP_PENALTY = 0.1;

// Player capsule + movement tuning (metres, seconds)
export const PLAYER_RADIUS = 0.35;
export const PLAYER_HEIGHT = 1.8;
export const PLAYER_EYE_HEIGHT = 1.62;
export const WALK_SPEED = 4.2;
export const SPRINT_SPEED = 6.4;
export const CROUCH_SPEED = 2.2;
export const JUMP_SPEED = 5.2;
export const GRAVITY = -16;
export const GROUND_ACCEL = 40;
export const AIR_ACCEL = 8;
export const GROUND_FRICTION = 10;
export const MAX_HP = 100;

// Stamina & movement tech
export const MAX_STAMINA = 100;
export const SPRINT_DRAIN_PER_S = 10;
export const JUMP_STAMINA_COST = 8;
export const SKIP_STAMINA_COST = 10;
export const STAMINA_REGEN_PER_S = 15;
export const STAMINA_REGEN_TICKS = 30; // 1.0 s at 30 Hz
/** Below this after hitting zero you are "winded" (slower) until you recover past it. */
export const WINDED_RECOVER_AT = 20;
export const WINDED_SPEED_MULT = 0.9;
/** Landing grace: ticks after touchdown during which friction is skipped, enabling skip-jumps. */
export const SKIP_WINDOW_TICKS = 3;
export const SKIP_SPEED_BONUS = 1.05;
export const SKIP_MAX_SPEED_MULT = 1.35;

// Mantle (ledge climb) & fall damage
/** Max ledge height above the feet that a jumping player can mantle onto. */
export const MANTLE_MAX_HEIGHT = 1.4;
/**
 * Min ledge height to bother mantling. Anything at or below this is a *step*, climbed by the
 * auto step-up in `stepPlayer` (see `STEP_UP`, which is deliberately the same number so there
 * is no dead band of ledges that neither step nor mantle).
 */
export const MANTLE_MIN_HEIGHT = 0.5;
/**
 * Highest box top a grounded player walks straight up onto, without a jump or a mantle. Before
 * 2026-09-04 this was effectively 0.05 m for boxes (while the terrain heightfield already
 * auto-stepped a full `STEP_DOWN`), which made every authored stair with sane risers an
 * impassable wall and forced map stairs into 1.28 m mantle risers - the "stairs are super
 * glitchy" playtest report. Keep it == MANTLE_MIN_HEIGHT.
 */
export const STEP_UP = MANTLE_MIN_HEIGHT;
/** How far in front of the capsule edge we probe for a ledge. */
export const MANTLE_REACH = 0.45;
/**
 * A mantle is a short scripted climb, not a teleport: the feet rise `MANTLE_RISE_PER_TICK`
 * per 30 Hz tick (5.4 m/s) until they reach the ledge, then the body lunges forward onto it
 * over `MANTLE_LUNGE_TICKS`. Integer ticks so client prediction and the server agree exactly;
 * a full-height (1.4 m) mantle takes 8 + 3 = 11 ticks ~= 0.37 s.
 */
export const MANTLE_RISE_PER_TICK = 0.18;
export const MANTLE_LUNGE_TICKS = 3;
/** Horizontal velocity retained per tick while mantling (the climb kills your run). */
export const MANTLE_DAMP = 0.6;
/** Impact speed (m/s, downward) below which landings are free. ~3 m drop. */
export const FALL_SAFE_SPEED = 9.8;
/** Damage per m/s of impact speed above the safe threshold. Lethal (100) at ~19.6 m/s (~12 m). */
export const FALL_DAMAGE_PER_MS = 10.2;

// Stances
export const Stance = { Stand: 0, Crouch: 1, Prone: 2 } as const;
export const PRONE_SPEED = 1.0;
export const PRONE_HEIGHT = 0.6;
export const CROUCH_HEIGHT = PLAYER_HEIGHT * 0.65;
export const PRONE_EYE_HEIGHT = 0.45;
export const CROUCH_EYE_HEIGHT = PLAYER_EYE_HEIGHT * 0.65;
/** Ticks a stance change takes (movement frozen, accuracy penalised): crouch<->stand, and any prone change. */
export const STANCE_TICKS_CROUCH = 6;
export const STANCE_TICKS_PRONE = 18;
/** Spread multipliers by stance index (stand, crouch, prone) and by movement state. */
export const STANCE_SPREAD_MULT = [1, 0.8, 0.6] as const;
export const STANCE_RECOIL_MULT = [1, 0.85, 0.7] as const;
export const MOVING_SPREAD_MULT = 1.6;
export const AIRBORNE_SPREAD_MULT = 2.5;
export const TRANSITION_SPREAD_MULT = 2.0;
/** Horizontal speed above which a player counts as "moving" for accuracy. */
export const MOVING_SPEED_THRESHOLD = 1.0;

// --- party ---------------------------------------------------------------------------------

/**
 * Five, including the leader. A tank, a healer and three others is the shape the two Callings
 * are balanced for, and it is small enough that the party frames are a column rather than a
 * second HUD.
 */
export const PARTY_MAX_SIZE = 5;
/**
 * How long an invitation stands before it is forgotten. Long enough to read a line of chat mid
 * fight, short enough that nobody joins a group they answered two zones ago.
 */
export const PARTY_INVITE_TTL_MS = 60_000;
/**
 * How near a party member has to be to be credited for somebody else's kill or cleansing.
 *
 * Sixty metres is "the same fight" — inside a Chorister camp, or up the road within shouting
 * distance — and outside it two people are simply questing separately with a shared frame list.
 */
export const PARTY_CREDIT_RANGE_M = 60;
/**
 * How near a member has to be for their frame to read as live rather than dimmed. Matches the
 * snapshot's own keep range, so a frame goes pale at the same moment the body stops updating.
 */
export const PARTY_FRAME_RANGE_M = 80;

/**
 * Deprecated, kept only so the client keeps compiling while its own v3 work lands. The dropship
 * insertion flyby is gone with the raid loop; both of these are dead and answer accordingly.
 *
 * @deprecated Removed in protocol v3. Delete the call sites, not this shim.
 */
export const INSERTION_MS = 0;
/** @deprecated Removed in protocol v3. Always false. */
export function hasInsertion(_mode: string, _mapId: string): boolean {
  return false;
}
